/**
 * Aviso de `/admin/random` cuando Random dejo de informar mas productos de
 * los que la sincronizacion borra sola (RND-01).
 *
 * El sitio borra por su cuenta hasta el 5% del catalogo; pasado eso no borra
 * nada, marca los productos y los saca de la tienda, y espera a que alguien
 * mire. Son dos casos que se ven igual desde aca y se resuelven distinto:
 *
 * - Random devolvio menos de lo que tiene (una falla sin error). No hay que
 *   hacer nada: la proxima corrida los trae de vuelta y el aviso desaparece.
 * - Random hizo una limpieza masiva de verdad. Entonces se acepta el borrado
 *   con el boton, que pasa por confirmacion porque no tiene vuelta atras: se
 *   van las fotos, las descripciones y el SEO de esos productos.
 *
 * Se borra lo que este marcado en el servidor al confirmar, no la cifra que se
 * mostro: entre una y otra pudo correr una sincronizacion.
 */
import { useState } from 'react';

import { eliminarProductosNoInformados } from '../../servicios/administracion.servicio';
import { numero } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';
import { Modal } from './Modal';
import { PanelAdmin } from './PanelAdmin';

export interface PanelNoInformadosProps {
  cantidad: number;
  /** Se dispara despues de borrar, para releer el estado de la integracion. */
  onEliminado(): void;
}

export function PanelNoInformados({ cantidad, onEliminado }: PanelNoInformadosProps) {
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  if (cantidad === 0) return null;

  function cerrar(): void {
    if (enviando) return;
    setConfirmando(false);
    setFalla(null);
  }

  async function eliminar(): Promise<void> {
    setEnviando(true);
    setFalla(null);
    try {
      await eliminarProductosNoInformados();
      setConfirmando(false);
      onEliminado();
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <PanelAdmin
        titulo="Productos que Random dejó de informar"
        bajada="Son más del 5% del catálogo, así que el sitio no los borró solo. Mientras tanto no se venden."
        variante="plano"
        extra={
          <span className="etq etq-alerta">
            <Icono nombre="alerta" />
            {numero(cantidad)} sin borrar
          </span>
        }
      >
        <p className="parrafo-panel">
          Si Random respondió incompleto, la próxima sincronización los trae de vuelta y este
          aviso desaparece solo. Si en Random se hizo una limpieza de artículos, acepta el
          borrado: se eliminan del sitio con sus fotos y descripciones.
        </p>
        <div className="modal-acciones">
          <button
            type="button"
            className="btn btn-sm btn-peligro"
            onClick={() => {
              setConfirmando(true);
            }}
          >
            <Icono nombre="alerta" />
            Borrar los {numero(cantidad)} productos
          </button>
        </div>
      </PanelAdmin>

      <Modal
        titulo="Borrar lo que Random ya no informa"
        bajada="No se puede deshacer"
        abierto={confirmando}
        onCerrar={cerrar}
      >
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        <p className="parrafo-panel">
          Se van a eliminar <strong>{numero(cantidad)} productos</strong> que Random dejó de
          mandar, con sus fotos, descripciones y SEO. Si Random los vuelve a informar después,
          entran de nuevo como productos nuevos, ocultos y sin nada de eso.
        </p>

        <div className="modal-acciones">
          <button type="button" className="btn btn-contorno" onClick={cerrar} disabled={enviando}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-peligro"
            onClick={() => {
              void eliminar();
            }}
            disabled={enviando}
          >
            {enviando ? 'Borrando…' : 'Borrar los productos'}
          </button>
        </div>
      </Modal>
    </>
  );
}
